/**
 * Which home modules a studio shows, and in what order (Phase 5).
 *
 * Stored as one comma-separated string on `StudioPreference.homeModules` —
 * the enabled ids, in display order. Null or unparseable means "everything, in
 * the default order", so an un-migrated environment renders exactly as it does
 * today.
 *
 * This is deliberately NOT `StudioModuleConfig`, which governs which
 * NAVIGATION AREAS a studio has (channels, events, resources…). Home modules
 * are widgets inside one page. Folding them together would make one setting
 * mean two things and one toggle remove a whole area.
 */
export const HOME_MODULES = ["composer", "pulse", "featured", "posts"] as const;

export type HomeModule = (typeof HOME_MODULES)[number];

/**
 * Stored when an owner switches every module off.
 *
 * "" and null already mean "unset — render everything", so an empty list needs
 * a value of its own or turning everything off would come back as turning
 * everything on. Kept for reading data written before every module off was
 * blocked at the point of toggling (see HomeModuleToggles) and rejected by
 * the server (see studio.ts) — parseHomeModules treats it as unset rather
 * than rendering a page with nothing on it.
 */
export const NO_HOME_MODULES = "none";

export const DEFAULT_HOME_MODULES: readonly HomeModule[] = HOME_MODULES;

export type HomeModuleOption = {
  id: HomeModule;
  name: string;
  description: string;
};

export const HOME_MODULE_OPTIONS = [
  {
    id: "composer",
    name: "Composer",
    description: "The prompt inviting members to start a post.",
  },
  {
    id: "pulse",
    name: "Pulse",
    description: "What is live, what is waiting on you, and what is next.",
  },
  {
    id: "featured",
    name: "Featured",
    description: "The pinned post, shown as a hero.",
  },
  {
    id: "posts",
    name: "Post stream",
    description: "The feed itself.",
  },
] satisfies readonly HomeModuleOption[];

function isHomeModule(value: string): value is HomeModule {
  return (HOME_MODULES as readonly string[]).includes(value);
}

/** Known ids only, first occurrence wins. Shared by parse and serialize. */
function dedupeKnownModules(ids: readonly string[]): HomeModule[] {
  const seen = new Set<HomeModule>();
  const result: HomeModule[] = [];
  for (const raw of ids) {
    const id = raw.trim();
    if (!isHomeModule(id) || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
}

/**
 * Parse the stored string into the modules to render, in order.
 *
 * Tolerant by design — this value is read on every home page render, and a
 * value written by a newer deploy, hand-edited, or left over from a rolled-back
 * release must degrade to a working feed rather than an empty page:
 *
 *   - unknown ids are dropped, so a module removed in a later release does not
 *     leave a hole;
 *   - duplicates collapse to the first occurrence;
 *   - null, empty, or a value with nothing recognisable in it falls back to
 *     every module in the default order;
 *   - an explicit "everything off" marker (predating the toggle/server guards
 *     against it — see NO_HOME_MODULES) falls back the same way, rather than
 *     rendering a page with nothing on it.
 */
export function parseHomeModules(
  value: string | null | undefined,
): HomeModule[] {
  if (!value || value === NO_HOME_MODULES) return [...DEFAULT_HOME_MODULES];

  const parsed = dedupeKnownModules(value.split(","));

  // Nothing recognisable — treat as unset rather than as "hide everything".
  if (parsed.length === 0) return [...DEFAULT_HOME_MODULES];

  return parsed;
}

/**
 * What to store for a given set of enabled modules, in the given order.
 *
 * Order is meaningful — an owner can move the composer below the pulse strip,
 * or either below the feed — so this preserves it rather than canonicalising.
 * It still drops unknown ids and collapses duplicates.
 */
export function serializeHomeModules(modules: readonly HomeModule[]): string {
  const ordered = dedupeKnownModules(modules);
  return ordered.length === 0 ? NO_HOME_MODULES : ordered.join(",");
}

/**
 * Every module in the order the settings list should show them: the enabled
 * ones where the owner put them, the switched-off ones in the slot they would
 * return to.
 *
 * The list must NOT reshuffle when a row is toggled. Appending the disabled
 * ones instead meant switching the composer off sent its row to the bottom, so
 * the switch under the cursor belonged to a different module a moment later —
 * which reads as "the toggle did nothing".
 *
 * Because a disabled row sits exactly where enableHomeModule would re-insert
 * it, toggling a row off and on leaves the whole list unchanged.
 */
export function homeModuleRows(enabled: readonly HomeModule[]): HomeModule[] {
  let rows = [...enabled];
  for (const id of HOME_MODULES) {
    if (!rows.includes(id)) rows = enableHomeModule(rows, id);
  }
  return rows;
}

/**
 * Switch a module on, at the position it belongs rather than the end.
 *
 * Appending was the bug: switching the composer off and back on put it after
 * the post stream, so it came back below the entire feed. Re-enabling inserts
 * it before the first module that follows it in HOME_MODULES order, which
 * restores its original slot when nothing else has moved and stays sensible
 * when something has.
 */
export function enableHomeModule(
  enabled: readonly HomeModule[],
  id: HomeModule,
): HomeModule[] {
  if (enabled.includes(id)) return [...enabled];

  const canonical = HOME_MODULES.indexOf(id);
  const before = enabled.findIndex(
    (other) => HOME_MODULES.indexOf(other) > canonical,
  );

  const next = [...enabled];
  next.splice(before === -1 ? next.length : before, 0, id);
  return next;
}

/**
 * Move a module one place up or down among the enabled ones.
 *
 * A no-op at either end rather than wrapping around — a control that silently
 * teleports the top item to the bottom is worse than one that does nothing.
 */
export function moveHomeModule(
  enabled: readonly HomeModule[],
  id: HomeModule,
  direction: -1 | 1,
): HomeModule[] {
  const from = enabled.indexOf(id);
  const to = from + direction;
  if (from === -1 || to < 0 || to >= enabled.length) return [...enabled];

  const next = [...enabled];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Whether a module should render, given the stored value. */
export function isHomeModuleEnabled(
  value: string | null | undefined,
  module: HomeModule,
): boolean {
  return parseHomeModules(value).includes(module);
}
